import type { Locale } from '@mfw/translations';

import {
  getConvertedValueForDB,
  getConvertedValueForUi,
  type UserUnitSettings,
} from '@mfw/common';
import { getLocalizedNameByLocale } from '@mfw/components';
import { v4 as uuidv4 } from 'uuid';
import omit from 'lodash/omit';

import type {
  ApiWorkoutGroupExercise,
  UiWorkoutExercise,
  UiWorkoutCreate,
  ApiWorkout,
  UiWorkout,
} from '../../schemas/workouts.schema';
import type { ApiContentConfigurationPayload } from '../../schemas/contentConfiguration.schema';
import type {
  ApiWorkoutFeedback,
  UiWorkoutFeedback,
  EditWorkoutArgs,
} from '../types/workouts.types';

import {
  mapContentConfigurationFromApi,
  mapContentConfigurationToApi,
} from './contentConfiguration.mappers';
import { mapExerciseTranslationsFromApi } from './exercises.mappers';
import { mapCategoryWithTagsFromApi } from './categories.mappers';
import { withPreferredDisplayUnit } from './configurable-units';
import { mapConfigurableFromApi } from './configurables.mappers';
import { FormatDate } from '../../utils';

type ApiWorkoutExercisePayload = {
  id: string;
  comment?: string | null;
  rounds: Array<{
    configurables: Array<{
      configurableId: string;
      configurableValue: string;
      isChecked?: boolean;
    }>;
  }>;
  alternates?: ApiWorkoutExercisePayload[];
};

// Accepts both a full `UiWorkoutExercise` (editing an already-loaded workout)
// and the narrower create-step shape (a brand new workout) — both agree on
// these fields, which is all this mapper touches.
type UiWorkoutExerciseLike = {
  id: string;
  comment?: string | null;
  rounds: UiWorkoutExercise['rounds'];
  alternates?: UiWorkoutExerciseLike[];
};

function mapWorkoutExerciseToApi(
  exercise: UiWorkoutExerciseLike,
): ApiWorkoutExercisePayload {
  return {
    id: exercise.id,
    comment: exercise.comment,
    rounds: exercise.rounds.map((round) => {
      return {
        configurables: round.configurables.map((configurable) => {
          const configurableValue: string = getConvertedValueForDB({
            configurableFormat: configurable.configurable.format,
            // back to the unit the value was LOADED from — see `dbUnit` on
            // `UiConfigurableSchema`; absent for a configurable that never
            // went through the load conversion (a freshly added exercise),
            // whose value is already in its own unit
            dbUnit: configurable.configurable.dbUnit,
            uiConfigurableUnit: configurable.configurable.unit,
            value: configurable.configurableValue,
          });
          return {
            configurableId: configurable.configurable.id,
            configurableValue,
            isChecked: configurable.isChecked,
          };
        }),
      };
    }),
    alternates: exercise.alternates?.map(mapWorkoutExerciseToApi),
  };
}

export function createWorkoutApiArgs(
  workout: Omit<UiWorkoutCreate, 'coverSource' | 'coverType'> & {
    coverSource: null | string;
    coverType: null | string;
  },
) {
  let tags: string[] = [];
  if (workout.categories && Object.keys(workout.categories).length) {
    tags = Object.values(workout.categories).flatMap((category) => {
      return category?.flatMap((tag) => tag.value) ?? [];
    });
  }

  const contentConfiguration: ApiContentConfigurationPayload =
    mapContentConfigurationToApi(workout);

  const args = omit(
    {
      ...workout,
      ...contentConfiguration,
      tags,
      translations: workout.translations.map((translation) => ({
        description: translation.description,
        locale: translation.locale?.[0]?.value,
        name: translation.name,
      })),
    },
    [
      'categories',
      'coverFile',
      'mediaFile',
      'coverSignedUrl',
      'mediaSignedUrl',
      'locale',
      'isMarketplace',
      'isOrg',
      // form metadata for the 9.6 price floor — never part of the payload
      'marketplaceListedPrice',
      'marketplacePrice',
      'organizationIds',
    ],
  );
  return {
    ...args,
    groups: args.groups?.map((group) => {
      return {
        exercises: group.exercises.map(mapWorkoutExerciseToApi),
        type: group.type,
        // WARMUP/COOLDOWN can't be renamed — withheld even if form state
        // somehow carries one, the backend refine would reject it otherwise.
        name: group.type === 'MAIN' ? (group.name ?? null) : null,
      };
    }),
    restTimes: {
      afterExercise: FormatDate.formatDateTimeToSeconds(
        workout.restTimes.afterExercise,
      ),
      afterGroup: FormatDate.formatDateTimeToSeconds(
        workout.restTimes.afterGroup,
      ),
      afterRound: FormatDate.formatDateTimeToSeconds(
        workout.restTimes.afterRound,
      ),
    },
  };
}

/**
 * Rebuilds the `EditWorkoutArgs` payload `useEditWorkoutById` needs from an
 * already-fetched `UiWorkout` — for callers that persist a small change (a
 * comment, an alternate, a removed exercise) without going through the
 * workout builder's form. There is no per-exercise API, only this
 * whole-workout `PATCH`, so every field the builder's own submit sends
 * (`WorkoutPageContent.tsx`'s `handleSubmit`) has to be forwarded here too.
 */
export function uiWorkoutToEditArgs(
  workout: UiWorkout,
  locale: Locale,
  equipmentCategoryId: string,
): EditWorkoutArgs {
  return {
    categories: omit(workout.categories, equipmentCategoryId),
    coverSource: workout.coverSource,
    coverType: workout.coverType ?? '',
    groups: workout.groups.map((group) => ({
      id: group.id,
      type: group.type,
      name: group.name,
      exercises: group.exercises.map(
        ({ id, comment, rounds, translations, alternates }) => ({
          id,
          comment,
          rounds,
          translations,
          alternates: alternates?.map(
            ({
              id: altId,
              comment: altComment,
              rounds: altRounds,
              translations: altTranslations,
            }) => ({
              id: altId,
              comment: altComment,
              rounds: altRounds,
              translations: altTranslations,
            }),
          ),
        }),
      ),
    })),
    id: workout.id,
    isMarketplace: workout.isMarketplace,
    isOrg: workout.isOrg,
    level: workout.level,
    locale,
    marketplaceCurrency: workout.marketplaceCurrency,
    marketplacePrice: workout.marketplacePrice,
    organizationId: workout.organizationId ?? undefined,
    organizationIds: workout.organizationIds,
    restTimes: workout.restTimes,
    translations: workout.translations,
  };
}

function mapWorkoutExerciseRoundsFromApi(
  rounds: ApiWorkoutGroupExercise['rounds'],
  userUnitSettings?: UserUnitSettings | null,
) {
  return rounds.map((round) => {
    return {
      // id: uuidv4(),
      id: round.workoutExerciseId,
      configurables: round.configurables.map((configurable) => {
        // ONE EXPRESSION OF THE RULE, shared with `buildExerciseFromCatalog` —
        // which rewrote the unit without recording `dbUnit` and corrupted every
        // catalog-added configurable by a factor of a thousand. See
        // `configurable-units.ts`.
        const uiConfigurable = withPreferredDisplayUnit(
          mapConfigurableFromApi(configurable),
          userUnitSettings,
        );
        const configurableUnit = uiConfigurable.dbUnit;
        const prefferedUnit = uiConfigurable.unit;
        // Convert units if user settings are provided.
        //
        // `format` IS NOT OPTIONAL HERE, even though the parameter is.
        // `getDecimalsBasedOnConfigurableFormat(undefined)` is 0, and the SAVE
        // side (`mapWorkoutExerciseToApi`) passes the configurable's real
        // format — so omitting it rounded the load to whole units and the save
        // to the declared precision, and the two no longer met. A DECIMAL2
        // weight went `'62.50' kg → 2205 oz → '62.51' kg`, which the backend
        // reads as a CHANGED prescription: `planWorkoutExercises` marks the
        // slot updated and `deleteWorkoutProgresses` cancels every assigned
        // client's in-flight session — for a request that renamed the workout.
        const configurableValue = getConvertedValueForUi({
          format: configurable.format,
          fromUnit: configurableUnit,
          toUnit: prefferedUnit,
          value: configurable.configurableValue,
        });
        return {
          configurable: uiConfigurable,
          configurableValue,
          isChecked: configurable.isChecked,
        };
      }),
      round: round.round,
      workoutExerciseId: round.workoutExerciseId,
    };
  });
}

// Matches exactly what this mapper produces — not `UiWorkoutExercise` itself,
// since the mapped `categories`/`translations`/`rounds` shapes don't line up
// 1:1 with that schema. That gap used to be papered over by a suppression on
// the `groups` field below; the schemas now describe the real shapes, so this
// type checks against them. Naming it explicitly here is what lets the function
// type itself instead of relying on (circular) return inference.
type MappedWorkoutExercise = {
  id: string;
  categories: ReturnType<typeof mapCategoryWithTagsFromApi>;
  comment: string | null;
  coverSignedUrl: null | string;
  coverType: null | string;
  mediaSignedUrl: string;
  mediaType: string;
  organizationId: ApiWorkoutGroupExercise['organizationId'];
  // The catalog columns the preview mount reads (`usePreviewOrganizationId`).
  isPlatform: boolean;
  isPremium: boolean;
  // Which rows the marketplace teaser withholds — decided by the server, read
  // back by the preview to blur them. Must survive every path that builds a
  // workout exercise, alternates included.
  previewOnly: ApiWorkoutGroupExercise['previewOnly'];
  rounds: ReturnType<typeof mapWorkoutExerciseRoundsFromApi>;
  translations: ReturnType<typeof mapExerciseTranslationsFromApi>;
  alternates?: MappedWorkoutExercise[];
  uniqueId: string;
};

function mapWorkoutExerciseFromApi(
  exercise: ApiWorkoutGroupExercise,
  locale: Locale,
  userUnitSettings?: UserUnitSettings | null,
): MappedWorkoutExercise {
  const mapped = {
    id: exercise.id,
    categories: mapCategoryWithTagsFromApi({
      categories: exercise.categories,
      locale,
    }),
    comment: exercise.comment,
    coverSignedUrl: exercise.coverSignedUrl,
    coverType: exercise.coverType,
    mediaSignedUrl: exercise.mediaSignedUrl as string,
    // TODO Remove OR condition. This is temporary since API does not return it here but it's required in schemas.
    mediaType: (exercise.mediaType as string) || 'video/mp4',
    organizationId: exercise.organizationId,
    isPlatform: exercise.isPlatform ?? false,
    isPremium: exercise.isPremium ?? false,
    previewOnly: exercise.previewOnly,
    rounds: mapWorkoutExerciseRoundsFromApi(exercise.rounds, userUnitSettings),
    translations: mapExerciseTranslationsFromApi(exercise.translations),
    alternates: exercise.alternates?.map((alternate) =>
      mapWorkoutExerciseFromApi(alternate, locale, userUnitSettings),
    ),
    uniqueId: uuidv4(),
  };
  return mapped;
}

export function mapWorkoutFromApi(
  workout: ApiWorkout,
  locale: Locale,
  userUnitSettings?: UserUnitSettings | null,
): UiWorkout {
  const categories = mapCategoryWithTagsFromApi({
    categories: workout.categories ?? [],
    locale,
  });

  const contentConfiguration = mapContentConfigurationFromApi(workout);

  return {
    id: workout.id,
    author: {
      // the list endpoint sends only the FK (`userId`), the detail endpoint the
      // joined `author` — either is enough for the marketplace preview gate
      id: workout?.author?.id ?? workout?.userId,
      name: workout?.author?.name || '',
    },
    categories,
    configuration: contentConfiguration,
    coverSignedUrl: workout?.coverSignedUrl,
    coverSource: workout?.coverSource,
    coverType: workout?.coverType,
    createdAt: workout.createdAt,
    groups:
      workout?.groups?.map((group) => {
        return {
          id: uuidv4(),
          exercises: group.exercises.map((exercise) =>
            mapWorkoutExerciseFromApi(exercise, locale, userUnitSettings),
          ),
          type: group.type,
          name: group.type === 'MAIN' ? (group.name ?? null) : null,
        };
      }) ?? [],
    isDeleted: workout.isDeleted,
    // Same duplication as the marketplace fields below, and the same reason —
    // but these two are `.optional()` in `UiContentConfigurationSchema`, so
    // omitting them type-checked and both stayed `undefined` for every mapped
    // workout. `WorkoutActions` labelled a listed workout "Post to
    // marketplace" off that `undefined`, and the program editor's
    // delete-exercise PATCH forwarded it as `enabled: false`, silently
    // delisting the workout. Read from the configuration block, which the
    // server derives from the `Marketplaces` join rather than from the
    // denormalised `isMarketplace` column (that column can drift; see
    // `trg_sync_is_marketplace` on the entity).
    isMarketplace: contentConfiguration.isMarketplace,
    isOrg: contentConfiguration.isOrg,
    level: workout.level,
    // Top-level marketplace fields (duplicated from configuration) — same pattern
    // as programs.mappers.ts/exercises.mappers.ts, needed by grid pages
    // and the marketplaceInfoFor helper for purchasing directly from the list.
    marketplaceCurrency: contentConfiguration.marketplaceCurrency,
    marketplaceId: contentConfiguration.marketplaceId,
    marketplaceInLibrary: contentConfiguration.marketplaceInLibrary,
    marketplaceRestorable: contentConfiguration.marketplaceRestorable,
    marketplaceLibraryId: contentConfiguration.marketplaceLibraryId,
    marketplacePrice: contentConfiguration.marketplacePrice,
    numberOfExercises: workout.numberOfExercises ?? 0,
    // Same duplication as the marketplace fields above, and the same reason:
    // `UiWorkout` merges `UiContentConfigurationSchema` at the top level, so
    // `organizationIds` is REQUIRED there. `programs.mappers.ts` and
    // `exercises.mappers.ts` both fill it; this mapper did not, and the
    // `@ts-ignore` on `groups` kept the whole literal from being checked.
    organizationIds: contentConfiguration.organizationIds,
    organizationId: workout?.organizationId,
    previewOnly: workout.previewOnly,
    progress: workout?.progress
      ? {
          id: workout.progress.id,
          lastViewedGroupIndex: workout.progress.lastViewedGroupIndex,
          lastViewedRoundIndex: workout.progress.lastViewedRoundIndex,
          lastViewedWorkoutExerciseId:
            workout.progress.lastViewedWorkoutExerciseId,
          progressMode: workout.progress.progressMode,
          status: workout.progress.status,
          workoutId: workout.progress.workoutId,
        }
      : undefined,
    restTimes: {
      afterExercise: FormatDate.fromSecondsToDateTime(
        workout?.restTimes?.afterExercise ?? 0,
      ),
      afterGroup: FormatDate.fromSecondsToDateTime(
        workout?.restTimes?.afterGroup ?? 0,
      ),
      afterRound: FormatDate.fromSecondsToDateTime(
        workout?.restTimes?.afterRound ?? 0,
      ),
    },
    tenantId: workout?.tenantId,
    translations: workout.translations?.map((translation) => ({
      id: translation.id,
      description: translation.description ?? '',
      locale: [
        {
          label: getLocalizedNameByLocale(translation.locale),
          value: translation.locale as Locale,
        },
      ],
      name: translation.name,
    })),
    uniqueId: uuidv4(),
    updatedAt: workout.updatedAt,
  };
}

export function mapWorkoutFeedbackFromApi(
  feedback: ApiWorkoutFeedback,
): UiWorkoutFeedback {
  return { ...feedback };
}
